import React, { FC, useContext } from "react";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import TextField from "Components/DesignSystem/TextField/TextField";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import { Add, Minus } from "Components/DesignSystem/Iconography/Icons";
import { OPERATORS } from "Common/GlobalConstant";
import { CSContext } from "../contexts/CSContext";

/**
 * The pivot-mapping rows on DS controls (2026-09-08): `Dropdown` (searchable for
 * the two attribute pickers), `TextField type="number"` for weightage, and
 * `IconButton` +/− (§ 212/§ 238; were hand-rolled 24px bordered squares).
 *
 * Every `handlePivotListChange` call keeps its legacy argument shape — the
 * selects hand over the raw VALUE string, and the weightage field hands over the
 * real change EVENT (the context handler calls `e.persist()` and reads
 * `e.target.value` for "weight"; DS `TextField` forwards the event as
 * `onChange`'s second argument). Row gating unchanged: everything disabled until
 * a category is picked, the first row's minus stays disabled, plus locks at
 * weightage sum ≥ 100.
 */
const CSAttributeMapping
    : FC = (props: any) => {
        const { pivotList, handlePivotListChange, attributeList, category, handleRowDelete, getWeightageSum, handleAdd } = useContext(CSContext)
        return (
            <>
                {
                    pivotList?.map((x: PivotType, index: number) => (
                        <div
                            id={`pivot-row-${index}`}
                            className="tw-flex tw-items-end tw-gap-4 tw-m-5"
                            key={index}
                        >
                            <div id={`pivot-row-${index}-pivot-attribute-container`} className="tw-w-[25%]">
                                <Dropdown
                                    id={`pivot-row-${index}-pivot-attribute-select`}
                                    label="Pivot Attribute"
                                    onChange={(value) => {
                                        handlePivotListChange(String(value), index, "pivot");
                                    }}
                                    value={x?.pivot_attribute}
                                    options={attributeList ?? []}
                                    placeholder="Select Attribute"
                                    disabled={category ? false : true}
                                    searchable
                                    usePortal
                                    data-testid={`pivot-row-${index}-pivot-attribute-select`}
                                />
                            </div>
                            <div id={`pivot-row-${index}-operator-container`} className="tw-w-[10%]">
                                <Dropdown
                                    id={`pivot-row-${index}-operator-select`}
                                    label="Operator"
                                    onChange={(value) => {
                                        handlePivotListChange(String(value), index, "operator");
                                    }}
                                    value={
                                        x?.operator !== "" ? x?.operator : OPERATORS[0]?.value
                                    }
                                    options={OPERATORS}
                                    disabled={category ? false : true}
                                    usePortal
                                    data-testid={`pivot-row-${index}-operator-select`}
                                />
                            </div>
                            <div id={`pivot-row-${index}-base-attribute-container`} className="tw-w-[25%]">
                                <Dropdown
                                    id={`pivot-row-${index}-base-attribute-select`}
                                    label="Base Attribute"
                                    onChange={(value) => {
                                        handlePivotListChange(String(value), index, "base");
                                    }}
                                    value={x?.base_attribute}
                                    options={attributeList ?? []}
                                    placeholder="Select Attribute"
                                    disabled={category ? false : true}
                                    searchable
                                    usePortal
                                    data-testid={`pivot-row-${index}-base-attribute-select`}
                                />
                            </div>
                            <div id={`pivot-row-${index}-weightage-container`} className="tw-w-[25%]">
                                <TextField
                                    id={`pivot-row-${index}-weightage-input`}
                                    label="Weightage"
                                    type="number"
                                    value={x?.weightage}
                                    placeholder="Enter Percentage"
                                    onChange={(_value, e) => {
                                        // The context's "weight" branch persists and reads the EVENT.
                                        if (e) handlePivotListChange(e, index, "weight");
                                    }}
                                    disabled={category ? false : true}
                                    data-testid={`pivot-row-${index}-weightage-input`}
                                />
                            </div>
                            <div id={`pivot-row-${index}-delete-container`} className="tw-pb-1">
                                <IconButton
                                    id={`pivot-row-${index}-delete-btn`}
                                    aria-label="Remove attribute mapping"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        handleRowDelete(index);
                                    }}
                                    disabled={index <= 0}
                                    data-testid={`pivot-row-${index}-delete-btn`}
                                >
                                    <Minus size={16} />
                                </IconButton>
                            </div>
                            <div id={`pivot-row-${index}-add-container`} className="tw-pb-1">
                                <IconButton
                                    id={`pivot-row-${index}-add-btn`}
                                    aria-label="Add attribute mapping"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleAdd(index)}
                                    disabled={getWeightageSum() >= 100}
                                    data-testid={`pivot-row-${index}-add-btn`}
                                >
                                    <Add size={16} />
                                </IconButton>
                            </div>
                        </div>
                    ))}
            </>
        );
    };

export default CSAttributeMapping;
